import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ArrowRight,
  TrendingUp,
  Target,
  DollarSign,
  Phone,
  PhoneCall,
  ExternalLink,
  Clock,
  Loader2,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { usePipelineMetrics } from '@/hooks/usePipelineMetrics';
import { usePipelineDealsDetail } from '@/hooks/usePipelineDealsDetail';
import { useConsultantContactStats, useConsultantSalesStats } from '@/hooks/useConsultantContactStats';
import type { DealWithContactAge } from '@/hooks/useConsultantContactStats';
import { useCurrentAppUser } from '@/hooks/useCurrentAppUser';
import DealsDetailModal from '@/components/dashboard/DealsDetailModal';
import ConsultantSourceBreakdown from '@/components/dashboard/ConsultantSourceBreakdown';

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

const ACTIVE_STAGES = ['new', 'contact', 'scheduled', 'proposal', 'negotiation'];
const STAGE_LABELS: Record<string, string> = {
  new: 'Novo',
  contact: 'Contato',
  scheduled: 'Reunião',
  proposal: 'Proposta',
  negotiation: 'Negociação',
};
const STAGE_COLORS: Record<string, string> = {
  new: 'hsl(var(--chart-1))',
  contact: 'hsl(var(--chart-2))',
  scheduled: 'hsl(var(--chart-3))',
  proposal: 'hsl(var(--chart-4))',
  negotiation: 'hsl(var(--chart-5))',
};

function getDaysBadgeClass(days: number): string {
  if (days >= 14) return 'bg-red-100 text-red-700 border-red-200';
  if (days >= 8) return 'bg-orange-100 text-orange-700 border-orange-200';
  if (days >= 4) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
  return 'bg-green-100 text-green-700 border-green-200';
}

export default function MyDashboard() {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());

  // Stage funnel modal state
  const [stageModalOpen, setStageModalOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState<string | null>(null);

  const { data: currentUser } = useCurrentAppUser();
  const consultantId = currentUser?.id ?? null;

  const salesStats = useConsultantSalesStats(selectedYear, selectedMonth);
  const contactStats = useConsultantContactStats();

  const pipelineFilters = useMemo(
    () => ({ consultantId }),
    [consultantId]
  );
  const { data: metrics, isLoading: metricsLoading } = usePipelineMetrics(pipelineFilters);

  const { data: stageDeals, isLoading: stageDealsLoading } = usePipelineDealsDetail(
    { ...pipelineFilters, stage: selectedStage, limit: 200 },
    stageModalOpen && !!selectedStage
  );

  const stageCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    ACTIVE_STAGES.forEach((s) => (counts[s] = 0));
    metrics?.stage_counts?.forEach((s) => { counts[s.stage] = s.count; });
    return counts;
  }, [metrics]);

  const totalActive = ACTIVE_STAGES.reduce((sum, s) => sum + stageCounts[s], 0);

  const pct = salesStats.goalAmount > 0
    ? Math.min(100, Math.round((salesStats.soldAmount / salesStats.goalAmount) * 100))
    : 0;
  const remaining = Math.max(0, salesStats.goalAmount - salesStats.soldAmount);

  const formatUSD = (v: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v);

  const handleStageClick = (stage: string) => {
    setSelectedStage(stage);
    setStageModalOpen(true);
  };

  const yearOptions = [selectedYear - 1, selectedYear, selectedYear + 1].filter(
    (y) => y >= 2024 && y <= now.getFullYear() + 1
  );

  const handleDealClick = (dealId: string) => {
    window.open(`/DealDetail?id=${dealId}`, '_blank');
  };

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      {/* Header */}
      <div className="border-b bg-background px-6 py-4">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold">Meu Painel</h1>
            {currentUser && (
              <p className="text-sm text-muted-foreground">{currentUser.display_name}</p>
            )}
          </div>

          {/* Month / year selector */}
          <div className="flex items-center gap-2">
            <Select
              value={String(selectedMonth)}
              onValueChange={(v) => setSelectedMonth(Number(v))}
            >
              <SelectTrigger className="h-8 w-[130px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTH_NAMES.map((name, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={String(selectedYear)}
              onValueChange={(v) => setSelectedYear(Number(v))}
            >
              <SelectTrigger className="h-8 w-[90px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {yearOptions.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex-1 p-6 space-y-6 max-w-5xl mx-auto w-full">
        {/* Stats row — 4 cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Vendido */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Total Vendido
              </CardTitle>
            </CardHeader>
            <CardContent>
              {salesStats.isLoading ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              ) : (
                <>
                  <p className="text-3xl font-bold">{formatUSD(salesStats.soldAmount)}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {salesStats.soldCount} deal{salesStats.soldCount !== 1 ? 's' : ''} no período
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Total Recebido */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Total Recebido
              </CardTitle>
            </CardHeader>
            <CardContent>
              {salesStats.isLoading ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              ) : (
                <>
                  <p className="text-3xl font-bold">{formatUSD(salesStats.receivedAmount)}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {salesStats.soldAmount > 0
                      ? `${Math.round((salesStats.receivedAmount / salesStats.soldAmount) * 100)}% do vendido`
                      : '—'}
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          {/* Meta */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Target className="h-4 w-4" />
                Meta
              </CardTitle>
            </CardHeader>
            <CardContent>
              {salesStats.isLoading ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              ) : salesStats.goalAmount === 0 ? (
                <p className="text-sm text-muted-foreground italic">Não cadastrada</p>
              ) : (
                <>
                  <p className="text-3xl font-bold">{formatUSD(salesStats.goalAmount)}</p>
                  <div className="h-2 bg-muted rounded-full mt-2 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        pct >= 100 ? 'bg-green-500' : pct >= 50 ? 'bg-blue-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">{pct}% atingido</p>
                </>
              )}
            </CardContent>
          </Card>

          {/* O que falta */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Target className="h-4 w-4" />
                O que falta
              </CardTitle>
            </CardHeader>
            <CardContent>
              {salesStats.isLoading ? (
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              ) : salesStats.goalAmount === 0 ? (
                <p className="text-sm text-muted-foreground italic">Sem meta</p>
              ) : remaining === 0 ? (
                <>
                  <p className="text-3xl font-bold text-green-600">$0</p>
                  <p className="text-sm text-green-600 mt-1 font-medium">Meta atingida! 🎉</p>
                </>
              ) : (
                <>
                  <p className="text-3xl font-bold text-red-600">{formatUSD(remaining)}</p>
                  <p className="text-sm text-muted-foreground mt-1">para atingir a meta</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Source breakdown */}
        <ConsultantSourceBreakdown consultantId={consultantId} year={selectedYear} month={selectedMonth} />

        {/* Funnel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <TrendingUp className="h-5 w-5 text-primary" />
              Funil de Clientes
              <span className="text-xs font-normal text-muted-foreground">
                — {totalActive} deal{totalActive !== 1 ? 's' : ''} ativo{totalActive !== 1 ? 's' : ''}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {metricsLoading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <div className="flex flex-wrap items-center justify-center gap-2">
                {ACTIVE_STAGES.map((stage, index) => (
                  <React.Fragment key={stage}>
                    <div className="flex flex-col items-center">
                      <div
                        className="w-24 h-20 rounded-lg flex flex-col items-center justify-center shadow-sm border cursor-pointer hover:shadow-md transition-shadow"
                        style={{
                          backgroundColor: `${STAGE_COLORS[stage]}15`,
                          borderColor: STAGE_COLORS[stage],
                        }}
                        onClick={() => handleStageClick(stage)}
                      >
                        <span
                          className="text-2xl font-bold"
                          style={{ color: STAGE_COLORS[stage] }}
                        >
                          {stageCounts[stage]}
                        </span>
                        <span className="text-xs text-muted-foreground text-center leading-tight px-1">
                          {STAGE_LABELS[stage]}
                        </span>
                      </div>
                    </div>
                    {index < ACTIVE_STAGES.length - 1 && (
                      <ArrowRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Inline deal lists — Última Tentativa + Contato Efetivo */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <InlineDealList
            icon={<Phone className="h-5 w-5 text-yellow-600" />}
            title="Última Tentativa"
            subtitle="Qualquer ligação (mais antigo primeiro)"
            deals={contactStats.dealsByLastAttempt}
            loading={contactStats.isLoading}
            onDealClick={handleDealClick}
          />
          <InlineDealList
            icon={<PhoneCall className="h-5 w-5 text-green-600" />}
            title="Contato Efetivo"
            subtitle="Ligações acima de 60s (mais antigo primeiro)"
            deals={contactStats.dealsByLastEffective}
            loading={contactStats.isLoading}
            onDealClick={handleDealClick}
          />
        </div>
      </div>

      {/* Stage deals modal */}
      <DealsDetailModal
        open={stageModalOpen}
        onOpenChange={setStageModalOpen}
        title={`Deals — ${selectedStage ? STAGE_LABELS[selectedStage] ?? selectedStage : ''}`}
        subtitle={`${stageDeals?.length ?? 0} deals neste estágio`}
        deals={stageDeals ?? []}
        loading={stageDealsLoading}
      />
    </div>
  );
}

// ── Inline deal list card ──────────────────────────────────────────────────────
function InlineDealList({
  icon,
  title,
  subtitle,
  deals,
  loading,
  onDealClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  deals: DealWithContactAge[];
  loading: boolean;
  onDealClick: (dealId: string) => void;
}) {
  const formatUSD = (v: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(v);

  return (
    <Card className="flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          {icon}
          {title}
          {!loading && (
            <Badge variant="secondary" className="text-xs">{deals.length} deals</Badge>
          )}
        </CardTitle>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </CardHeader>
      <CardContent className="flex-1 pt-0">
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : deals.length === 0 ? (
          <div className="flex items-center justify-center h-24 text-muted-foreground text-sm">
            Nenhum deal ativo.
          </div>
        ) : (
          <ScrollArea className="h-[400px]">
            <div className="space-y-2">
              {deals.map((deal) => {
                const stage = (deal.stage ?? '').toLowerCase();
                const stageLabel = STAGE_LABELS[stage] ?? stage;
                const stageColor = STAGE_COLORS[stage] ?? 'hsl(var(--muted))';
                const contactName = deal.contact
                  ? `${deal.contact.first_name ?? ''} ${deal.contact.last_name ?? ''}`.trim() || '—'
                  : '—';

                return (
                  <div
                    key={deal.id}
                    onClick={() => onDealClick(deal.id)}
                    className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 cursor-pointer transition-colors group"
                  >
                    {/* Days badge */}
                    <div
                      className={`flex-shrink-0 w-14 h-12 rounded-lg border flex flex-col items-center justify-center text-center ${getDaysBadgeClass(deal.days_since)}`}
                    >
                      <span className="text-lg font-bold leading-none">{deal.days_since}</span>
                      <span className="text-[10px] leading-none">dias</span>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm truncate">{contactName}</span>
                        <Badge
                          variant="outline"
                          className="text-xs flex-shrink-0"
                          style={{ borderColor: stageColor, color: stageColor }}
                        >
                          {stageLabel}
                        </Badge>
                        <ExternalLink className="h-3 w-3 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                      </div>
                      {deal.sdr_name && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          SDR: <span className="font-medium">{deal.sdr_name}</span>
                        </p>
                      )}
                      {deal.last_date ? (
                        <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                          <Clock className="h-3 w-3" />
                          {format(deal.last_date, "dd/MM/yy 'às' HH:mm", { locale: ptBR })}
                        </div>
                      ) : (
                        <p className="text-xs text-muted-foreground mt-0.5 italic">Sem registro de contato</p>
                      )}
                    </div>

                    {/* Amount */}
                    {deal.total_amount != null && deal.total_amount > 0 && (
                      <span className="text-sm font-semibold text-primary flex-shrink-0">
                        {formatUSD(deal.total_amount)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        )}
      </CardContent>
    </Card>
  );
}
